import {
  Modal,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Button, Modal as ModalRNP, Portal, Text } from 'react-native-paper';
import { Colors } from '../themes';

type CustomAlertProps = {
  visible: boolean;
  hideAlert: () => void;
  title?: string | React.ReactNode;
  primaryButtonLabel?: string; // red button
  secondaryButtonLabel: string; // white button
  tertiaryButtonLabel?: string; // black border button
  action?: () => void;
  tertiaryAction?: () => void | Promise<void>;
  containerStyle?: StyleProp<ViewStyle>;
  showNativeModal?: boolean;
  children?: React.ReactNode;
};

const ContainerModal = (props: CustomAlertProps) => {
  if (!props.showNativeModal) {
    return (
      <Portal>
        <ModalRNP
          visible={props.visible}
          onDismiss={props.hideAlert}
          dismissable={false}
          contentContainerStyle={[styles.containerStyle, props.containerStyle]}
        >
          {props?.children}
        </ModalRNP>
      </Portal>
    );
  }
  return (
    <Modal
      visible={props.visible}
      onRequestClose={props.hideAlert}
      transparent={true}
      animationType="fade"
    >
      <View style={styles.backdrop} onTouchEnd={props.hideAlert}>
        <View style={[styles.containerStyle, props.containerStyle]}>
          {props.children}
        </View>
      </View>
    </Modal>
  );
};

export const CustomAlert = (props: CustomAlertProps) => {
  const handlePrimaryAlertAction = () => {
    props.action && props.action();
    props.hideAlert();
  };

  const handleTertiaryAlertAction = async () => {
    // Run the action first, then hide the alert
    if (props.tertiaryAction) {
      await props.tertiaryAction();
    }
    props.hideAlert();
  };

  return (
    <ContainerModal {...props}>
      {props?.title && (
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{props.title}</Text>
        </View>
      )}
      {props.action && (
        <Button
          style={[styles.button, styles.primaryButton]}
          textColor={Colors.white}
          labelStyle={styles.buttonLabel}
          onPress={handlePrimaryAlertAction}
        >
          {props.primaryButtonLabel}
        </Button>
      )}
      {props.tertiaryAction && props.tertiaryButtonLabel && (
        <Button
          style={[styles.button, styles.tertiaryButton]}
          textColor={Colors.black}
          labelStyle={styles.buttonLabel}
          onPress={handleTertiaryAlertAction}
        >
          {props.tertiaryButtonLabel}
        </Button>
      )}
      <Button
        style={[styles.button, styles.secondaryButton]}
        textColor={Colors.black}
        labelStyle={styles.buttonLabel}
        onPress={props.hideAlert}
      >
        {props.secondaryButtonLabel && props.secondaryButtonLabel}
      </Button>
    </ContainerModal>
  );
};

const styles = StyleSheet.create({
  containerStyle: {
    alignSelf: 'center',
    backgroundColor: Colors.white,
    paddingHorizontal: 20,
    paddingVertical: 24,
    marginHorizontal: 20,
    borderRadius: 12,
    minWidth: 350,
    justifyContent: 'flex-start',
    ...Platform.select({
      web: {
        width: 400,
        maxHeight: 300,
      },
    }),
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleContainer: {
    marginBottom: 24,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '400',
  },
  button: {
    borderWidth: 1,
    borderRadius: 8,
    height: 44,
    justifyContent: 'center',
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '600',
    textAlignVertical: 'center',
  },
  primaryButton: {
    marginBottom: 12,
    backgroundColor: Colors.red,
    color: Colors.white,
  },
  secondaryButton: {
    borderWidth: 0,
  },
  tertiaryButton: {
    marginBottom: 12,
    borderColor: Colors.black,
  },
});

export default CustomAlert;
